import * as Yup from 'yup';
import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import { DatePickerInput } from 'src/components/date-picker/date-picker-input';
import FormSectionPage from '../form-section/formSectionPage';
import i18n from 'src/localization/i18n';
import { isSiteCanadian } from 'src/utils/helpers';
import { useFormik } from 'formik';
import {
    CREDIT_APPLICANT_ZIP_CA_ERROR,
    CREDIT_APPLICANT_ZIP_US_ERROR,
    TITLE_REQUEST_ADDRESS_LINE1_ERROR,
    TITLE_REQUEST_ADDRESS_LINE2_ERROR,
    TITLE_REQUEST_CITY_ERROR,
} from 'src/localization/resouceKeys';
import {
    formatPostalCode_CA,
    formatPostalCode_US,
} from 'src/utils/formatInputs';

import Input from 'src/components/forms/input';
import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';

interface ITitleInformation {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    purchaseDate: string;
    odometerMileage: string;
    odometerDate: string;
}

interface IProps {
    onSave: (values: ITitleInformation) => void;
    isEnabled?: boolean;
    onFormTouched?: () => void;
}

const DATE_PICKER_MIN_DATE = new Date('1900-01-01');
const MAX_ADDRESS_LENGTH = 30;
const MAX_ADDRESS2_LENGTH = 30;
const MAX_CITY_LENGTH = 30;
const ADDRESS_REGEX = /^[A-Za-z0-9 '&/,.:\-]*$/;
const ODOMETER_MAX_DIGITS = 6;
const ODOMETER_REGEX = /^[1-9]{1}[0-9]{0,5}$/;
const MAX_ZIP_LENGTH_US = 10;
const MAX_ZIP_LENGTH_CA = 7;

const US_STATE_OPTIONS = [
    { value: 'AK', label: 'AK' },
    { value: 'AL', label: 'AL' },
    { value: 'AR', label: 'AR' },
    { value: 'AZ', label: 'AZ' },
    { value: 'CA', label: 'CA' },
    { value: 'CO', label: 'CO' },
    { value: 'CT', label: 'CT' },
    { value: 'DC', label: 'DC' },
    { value: 'DE', label: 'DE' },
    { value: 'FL', label: 'FL' },
    { value: 'GA', label: 'GA' },
    { value: 'HI', label: 'HI' },
    { value: 'IA', label: 'IA' },
    { value: 'ID', label: 'ID' },
    { value: 'IL', label: 'IL' },
    { value: 'IN', label: 'IN' },
    { value: 'KS', label: 'KS' },
    { value: 'KY', label: 'KY' },
    { value: 'LA', label: 'LA' },
    { value: 'MA', label: 'MA' },
    { value: 'MD', label: 'MD' },
    { value: 'ME', label: 'ME' },
    { value: 'MI', label: 'MI' },
    { value: 'MN', label: 'MN' },
    { value: 'MO', label: 'MO' },
    { value: 'MS', label: 'MS' },
    { value: 'MT', label: 'MT' },
    { value: 'NC', label: 'NC' },
    { value: 'ND', label: 'ND' },
    { value: 'NE', label: 'NE' },
    { value: 'NH', label: 'NH' },
    { value: 'NJ', label: 'NJ' },
    { value: 'NM', label: 'NM' },
    { value: 'NV', label: 'NV' },
    { value: 'NY', label: 'NY' },
    { value: 'OH', label: 'OH' },
    { value: 'OK', label: 'OK' },
    { value: 'OR', label: 'OR' },
    { value: 'PA', label: 'PA' },
    { value: 'RI', label: 'RI' },
    { value: 'SC', label: 'SC' },
    { value: 'SD', label: 'SD' },
    { value: 'TN', label: 'TN' },
    { value: 'TX', label: 'TX' },
    { value: 'UT', label: 'UT' },
    { value: 'VA', label: 'VA' },
    { value: 'VT', label: 'VT' },
    { value: 'WA', label: 'WA' },
    { value: 'WI', label: 'WI' },
    { value: 'WV', label: 'WV' },
    { value: 'WY', label: 'WY' },
];

const CA_PROVINCE_OPTIONS = [
    { value: 'AB', label: 'AB' },
    { value: 'BC', label: 'BC' },
    { value: 'MB', label: 'MB' },
    { value: 'NB', label: 'NB' },
    { value: 'NL', label: 'NL' },
    { value: 'NS', label: 'NS' },
    { value: 'NT', label: 'NT' },
    { value: 'NU', label: 'NU' },
    { value: 'ON', label: 'ON' },
    { value: 'PE', label: 'PE' },
    { value: 'QC', label: 'QC' },
    { value: 'SK', label: 'SK' },
    { value: 'YT', label: 'YT' },
];

const getTodayDate = (): Date => {
    const today = new Date();
    // eslint-disable-next-line no-magic-numbers
    today.setHours(23, 59, 59, 999);
    return today;
};

const getYesterdayDate = (): Date => {
    const yesterday = new Date();
    // eslint-disable-next-line no-magic-numbers
    yesterday.setDate(yesterday.getDate() - 1);
    // eslint-disable-next-line no-magic-numbers
    yesterday.setHours(23, 59, 59, 999);
    return yesterday;
};

const isTodayOrFutureDate = (dateValue: string): boolean => {
    if (!dateValue) {
        return false;
    }

    const selectedDate = new Date(dateValue);
    const today = new Date();

    // eslint-disable-next-line no-magic-numbers
    selectedDate.setHours(0, 0, 0, 0);
    // eslint-disable-next-line no-magic-numbers
    today.setHours(0, 0, 0, 0);

    return selectedDate.getTime() >= today.getTime();
};

const TitleInformation: React.FC<IProps> = ({
    onSave,
    isEnabled = false,
    onFormTouched,
}) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [showIcon, setShowIcon] = useState<boolean | null>(null);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const isCanada = isSiteCanadian();
    const stateOptions = isCanada ? CA_PROVINCE_OPTIONS : US_STATE_OPTIONS;

    const schema = useMemo(
        () =>
            Yup.object({
                addressLine1: Yup.string()
                    .transform((value) => value?.trim())
                    .required(() => i18n.t(TITLE_REQUEST_ADDRESS_LINE1_ERROR))
                    .max(MAX_ADDRESS_LENGTH, () =>
                        i18n.t(TITLE_REQUEST_ADDRESS_LINE1_ERROR),
                    )
                    .matches(ADDRESS_REGEX, () =>
                        i18n.t(TITLE_REQUEST_ADDRESS_LINE1_ERROR),
                    ),
                addressLine2: Yup.string()
                    .transform((value) => value?.trim())
                    .max(MAX_ADDRESS2_LENGTH, () =>
                        i18n.t(TITLE_REQUEST_ADDRESS_LINE2_ERROR),
                    )
                    .matches(ADDRESS_REGEX, () =>
                        i18n.t(TITLE_REQUEST_ADDRESS_LINE2_ERROR),
                    ),
                city: Yup.string()
                    .transform((value) => value?.trim())
                    .required(() => i18n.t(TITLE_REQUEST_CITY_ERROR))
                    .max(MAX_CITY_LENGTH, () =>
                        i18n.t(TITLE_REQUEST_CITY_ERROR),
                    )
                    .matches(ADDRESS_REGEX, () =>
                        i18n.t(TITLE_REQUEST_CITY_ERROR),
                    ),
                state: Yup.string().required('Field required'),
                zipCode: Yup.string()
                    .required(() =>
                        i18n.t(
                            isCanada
                                ? CREDIT_APPLICANT_ZIP_CA_ERROR
                                : CREDIT_APPLICANT_ZIP_US_ERROR,
                        ),
                    )
                    .matches(
                        isCanada
                            ? /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
                            : /^(?!00000)(^\d{5})+(-\d{4})*$/i,
                        () =>
                            i18n.t(
                                isCanada
                                    ? CREDIT_APPLICANT_ZIP_CA_ERROR
                                    : CREDIT_APPLICANT_ZIP_US_ERROR,
                            ),
                    ),
                purchaseDate: Yup.string()
                    .required('Purchase date is required.')
                    .test(
                        'purchase-date-valid',
                        'Purchase date cannot be today or a future date.',
                        (value) => {
                            if (!value) {
                                return true;
                            }

                            return !isTodayOrFutureDate(value);
                        },
                    ),
                odometerMileage: Yup.string().matches(ODOMETER_REGEX, {
                    message:
                        'Odometer mileage must be a number between 1 and 6 digits.',
                    excludeEmptyString: true,
                }),
                odometerDate: Yup.string().test(
                    'odometer-date-valid',
                    'Odometer date cannot be a future date.',
                    (value) => {
                        if (!value) {
                            return true;
                        }

                        return !isTodayOrFutureDate(value);
                    },
                ),
            }),
        [],
    );

    const formik = useFormik<ITitleInformation>({
        initialValues: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: stateOptions[0]?.value || '',
            zipCode: '',
            purchaseDate: '',
            odometerMileage: '',
            odometerDate: '',
        },
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: (values) => {
            onSave(values);
            setShowIcon(true);
            setOpen(false);
            setIsDisabled(true);
        },
    });

    useEffect(() => {
        if (formik.dirty && !isDisabled && isEnabled) {
            onFormTouched?.();
        }
    }, [formik.dirty, isDisabled, isEnabled, onFormTouched]);

    const hasError = (field: keyof ITitleInformation) =>
        !!(formik.touched[field] && formik.errors[field]);

    const handleOdometerChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const regex = new RegExp(`^[0-9]{0,${ODOMETER_MAX_DIGITS}}$`);
        const { value } = event.target;
        if (regex.test(value)) {
            formik.setFieldValue('odometerMileage', value);
        }
    };

    const handleZipCodeChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { name, value } = event.target;
        let formattedZip = value.trim().toUpperCase();
        if (isCanada) {
            formattedZip = formatPostalCode_CA(formattedZip);
        } else {
            formattedZip = formatPostalCode_US(formattedZip);
        }
        event.target.name = name;
        event.target.value = formattedZip;
        formik.handleChange(event);
    };

    const handleEdit = () => {
        setIsDisabled(false);
        setOpen(true);
        setShowIcon(null);
        formik.setTouched({});
        formik.setErrors({});
    };

    useEffect(() => {
        if (isEnabled) {
            setOpen(true);
        }
    }, [isEnabled]);

    return (
        <FormSectionPage
            title={'Title Information'}
            handlerSubmit={formik.submitForm}
            isOpen={isOpen}
            setOpen={setOpen}
            showIcon={showIcon}
            isDisabled={isDisabled}
            onEdit={handleEdit}
            isFormValid={formik.isValid}
            isBlocked={!isEnabled}
        >
            <div className='o-layout'>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='addressLine1'
                        labelName={'Address Line 1*'}
                        value={formik.values.addressLine1}
                        handlerChange={formik.handleChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('addressLine1')
                                ? formik.errors.addressLine1
                                : ''
                        }
                        asTouched={formik.touched.addressLine1}
                        disabled={isDisabled || !isEnabled}
                        maxlength={MAX_ADDRESS_LENGTH}
                    />
                </div>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='addressLine2'
                        labelName={'Address Line 2'}
                        value={formik.values.addressLine2}
                        handlerChange={formik.handleChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('addressLine2')
                                ? formik.errors.addressLine2
                                : ''
                        }
                        asTouched={formik.touched.addressLine2}
                        disabled={isDisabled || !isEnabled}
                        maxlength={MAX_ADDRESS2_LENGTH}
                    />
                </div>
            </div>

            <div className='o-layout'>
                <div className='o-layout__item u-1/3 u-1/1@xs'>
                    <Input
                        type='text'
                        name='city'
                        labelName={'City*'}
                        value={formik.values.city}
                        handlerChange={formik.handleChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={hasError('city') ? formik.errors.city : ''}
                        asTouched={formik.touched.city}
                        disabled={isDisabled || !isEnabled}
                        maxlength={MAX_CITY_LENGTH}
                    />
                </div>
                <div className='o-layout__item u-1/3 u-1/1@xs title-information-state-field'>
                    <div className='c-form-field u-mb-large u-mt-large'>
                        <label
                            htmlFor='stateSelector'
                            className='c-form-field__label'
                        >
                            State*
                        </label>
                        <div className='c-form-field__box'>
                            <div className='c-input'>
                                <ComboBox
                                    name={'stateSelector'}
                                    options={stateOptions}
                                    placeholder={'Select'}
                                    selectedOption={formik.values.state}
                                    onSelect={(value: string) => {
                                        formik.setFieldValue('state', value);
                                        formik.setFieldTouched(
                                            'state',
                                            true,
                                            false,
                                        );
                                    }}
                                    disabled={isDisabled || !isEnabled}
                                    isError={hasError('state')}
                                />
                            </div>
                            {hasError('state') ? (
                                <span className='c-error-message'>
                                    {formik.errors.state}
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className='o-layout__item u-1/3 u-1/1@xs'>
                    <Input
                        type='text'
                        name='zipCode'
                        labelName={'Zip Code*'}
                        value={formik.values.zipCode}
                        handlerChange={handleZipCodeChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('zipCode') ? formik.errors.zipCode : ''
                        }
                        asTouched={formik.touched.zipCode}
                        disabled={isDisabled || !isEnabled}
                        maxlength={
                            isCanada ? MAX_ZIP_LENGTH_CA : MAX_ZIP_LENGTH_US
                        }
                    />
                </div>
            </div>

            <div className='o-layout'>
                <div className='o-layout__item u-1/3 u-1/1@xs'>
                    <div className='c-form-field  u-mb-large u-mt-large'>
                        <label
                            className='c-form-field__label'
                            htmlFor='title-information-purchase-date'
                        >
                            Purchase Date*
                        </label>
                        <div id='title-information-purchase-date'>
                            <DatePickerInput
                                selectedDate={formik.values.purchaseDate}
                                minDate={DATE_PICKER_MIN_DATE}
                                maxDate={getYesterdayDate()}
                                disabled={isDisabled || !isEnabled}
                                handleOnClose={(dateValue) => {
                                    formik.setFieldValue(
                                        'purchaseDate',
                                        dateValue,
                                    );
                                    formik.setFieldTouched(
                                        'purchaseDate',
                                        true,
                                    );
                                }}
                            />
                        </div>
                        {hasError('purchaseDate') ? (
                            <span className='c-error-message'>
                                {formik.errors.purchaseDate}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className='o-layout__item u-1/3 u-1/1@xs'>
                    <Input
                        type='text'
                        name='odometerMileage'
                        labelName={'Odometer Mileage'}
                        value={formik.values.odometerMileage}
                        handlerChange={handleOdometerChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('odometerMileage')
                                ? formik.errors.odometerMileage
                                : ''
                        }
                        asTouched={formik.touched.odometerMileage}
                        disabled={isDisabled || !isEnabled}
                        maxlength={ODOMETER_MAX_DIGITS}
                    />
                </div>
                <div className='o-layout__item u-1/3 u-1/1@xs'>
                    <div className='c-form-field  u-mb-large u-mt-large'>
                        <label
                            className='c-form-field__label'
                            htmlFor='title-information-odometer-date'
                        >
                            Odometer Date
                        </label>
                        <div id='title-information-odometer-date'>
                            <DatePickerInput
                                selectedDate={formik.values.odometerDate}
                                minDate={DATE_PICKER_MIN_DATE}
                                maxDate={getTodayDate()}
                                disabled={isDisabled || !isEnabled}
                                handleOnClose={(dateValue) => {
                                    formik.setFieldValue(
                                        'odometerDate',
                                        dateValue,
                                    );
                                    formik.setFieldTouched(
                                        'odometerDate',
                                        true,
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </FormSectionPage>
    );
};

export default TitleInformation;
